import { Injectable, OnInit } from '@angular/core';
import { RoleType } from '../raintree/interfaces/roles';
import { NavigationEnd, Router } from '@angular/router';
import { RoutePermission } from '../raintree/interfaces/permission.interface';
import { RequestMapper } from '../../request-mapper';

import { catchError, filter, Observable, of, switchMap } from 'rxjs';
import { SatesListService } from '../raintree/Transmission/sates-list.service';
import { DistrictListService } from '../raintree/Transmission/district-list.service';

export interface StateOutputFormat {
  districtPerms: Record<string, string[]>
  allDistrictsPerm: string[];
}
export interface CountryOutputFormat {
  statePerms: Record<string, StateOutputFormat>
  allStatesPerm: string[];
}

interface navType {
  label: string;
  icon: string;
  imageUrl: string;
  activeImageUrl: string;
  webRoute: string;
  subNav?: navType[];
};
@Injectable({
  providedIn: 'root',
})
export class AppConfigService {
  private routerSubscription: any;
  private currentUrl: string = '';
  private fullUrl: string[] = [];
  public state: string | null = null;
  public district: string | null = null;
  public landingRoute: string = '';
  public stateDistrictMap: Record<string, string[]> = {};
  viewExceptionsList = ['update'];
  constructor(
    private router: Router,
    public stateTransmissionService: SatesListService,
    public districtListService: DistrictListService
  ) {
    this.routerSubscription = this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event: NavigationEnd) => {
      });
  }
  public getAllStates(): Observable<string[]> {
    if (Object.keys(this.stateDistrictMap).length > 0) {
      return of(Object.keys(this.stateDistrictMap));
    }
    else {
      return this.stateTransmissionService.getStatesList().pipe(
        switchMap((response: any) => {
          if (!response.error) {
            return of(response.data.map((state: any) => state.stateName));
          }
          else {
            return of([]);
          }
        }),
        catchError((error) => {
          return of([]);
        }),
      )
    }
  }
  public getAllDistricts(state: string): Observable<string[]> {
    if (this.stateDistrictMap[state]?.length > 0) {
      return of(this.stateDistrictMap[state]);
    }
    else {
      return this.districtListService.getDistrictList(state).pipe(
        switchMap((response: any) => {
          if (!response.error) {
            this.stateDistrictMap[state] = response.data.map((dis: any) => dis.district_name);
            return of(this.stateDistrictMap[state]);
          }
          else {
            return of([]);
          }
        }),
        catchError((error) => {
          return of([]);
        })
      )
    }
  }
  ngOnInit() {
  }
  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
  public getPort() {
    return window.location.port;
  }
  public getAllPermissions(): RoutePermission[] {
    return JSON.parse(decodeURIComponent(atob(localStorage.getItem('permissions')!)));
  }
  // function to fetch all V3 Permissions
  public getAllPermissionsV3(): any[] {
    return JSON.parse(decodeURIComponent(atob(localStorage.getItem('permissionsV3')!)));
  }
  public fetchPermissionv3ByViewName(): Record<string, CountryOutputFormat> {
    this.currentUrl = this.router.url;
    this.fullUrl = this.currentUrl.split('?')[0].split('/').filter(Boolean);
    const data: Record<string, any> = this.getAllPermissionsV3();
    let result: Record<string, CountryOutputFormat> = {};
    const matchedObject = this.matchRoute(data, this.fullUrl);
    if (matchedObject) {
      result = this.extractCountryStateDistrictPerm(matchedObject);
    }
    return result;
  }
  // find the list of states and districts from the last object found from routes
  public extractCountryStateDistrictPerm(obj: Record<string, any>): Record<string, CountryOutputFormat> {
    let myPermission: Record<string, CountryOutputFormat> = {};
    const defaultObj = obj?.['default'];
    if (defaultObj?.PERMISSION) {
      const permissionData = defaultObj?.PERMISSION as
        Record< // Countries
          string,
          Record< // State | ALL
            string,
            Record< // District | ALL
              string,
              string[]
            > | string[]
          >
        >;
      Object.entries(permissionData).forEach(([country, statesData]) => { // for each country
        // extract PERMS given for entire country ('ALL' states)
        let allStatesPerm: string[] = statesData['ALL'] ? statesData['ALL'] as string[] : [];
        // initialize the value of permission for the country
        let myCountryPermission: CountryOutputFormat = {
          statePerms: {},
          allStatesPerm: allStatesPerm
        }
        // finding the statePerms
        Object.entries(statesData).forEach(([state, districtsData]) => { // for each state
          if (state != 'ALL') {
            // extract PERMS given for the entire state ('ALL' districts)
            const thisDistrictData = districtsData as Record<string, string[]>;
            let allDistrictsPerm: string[] = thisDistrictData['ALL'] ?? []; // permission for ALL districts
            // get the permissions in other given district
            let districtPerms: Record<string, string[]> = {};
            Object.entries(thisDistrictData).forEach(([district, value]) => {
              // setting the permission for the given district
              if (district != 'ALL') districtPerms[district] = value;
            })
            // initialize the value of permission for the country
            let myStatePermission: StateOutputFormat = {
              districtPerms: districtPerms,
              allDistrictsPerm: allDistrictsPerm
            }
            // setting the permission for the given state
            myCountryPermission.statePerms[state] = myStatePermission;
          }
        })
        // setting the permission for the given country
        myPermission[country] = myCountryPermission;
      })
    }
    return myPermission;
  }
  // find the last object according to route recursively
  private matchRoute(
    obj: Record<string, any>,
    parts: string[],
    index = 0
  ): Record<string, any> | null {
    // reached end of fullUrl || the obj is the last element in perm (not object but string)
    if (index >= parts.length || typeof obj !== 'object') return obj;
    // get the matching entry from the array of permissions by view
    const matchedEntry = Object.entries(obj).find(
      ([, child]) => child?.default?.webRoute === parts[index]
    );
    return matchedEntry ?
      this.matchRoute(matchedEntry[1], parts, index + 1) : null;
  }
  // getLandingPage by V3
  public getLadingRoute(): any {
    let landingPermNew: any = Object.values(this.getAllPermissionsV3())[0];
    this.landingRoute = '';
    this.fetchLandingView(landingPermNew);
    return this.landingRoute;
  }
  public fetchLandingView(viewDetails: any) {
    this.landingRoute += viewDetails.default.webRoute ?? '';
    const keys = Object.keys(viewDetails);
    for (let i = 0; i < keys.length; i++) {
      const item = keys[i];
      const value = viewDetails[item];
      if (item !== 'default') {
        this.landingRoute += '/';
        this.fetchLandingView(value);
        break;
      }
    }
  }
  public getSideNavigation(themeStyles: any): any {
    const permissions = this.getAllPermissionsV3();
    const navBarPermittedList: any = this.getSubViewV3(permissions, themeStyles);
    return navBarPermittedList
  }
  public getSubViewV3(view: any, themeStyles: any): any {
    let subNavigation: navType[] = [];
    const keys = Object.keys(view);
    for (let i = 0; i < keys.length; i++) {
      const item = keys[i];
      if (item !== 'default' && view[item].default.viewType == 'C') {
        const subView = this.getSubViewV3(view[item], themeStyles);
        subNavigation.push({
          label: view[item].default.labelName,
          icon: view[item].default.icon,
          imageUrl: themeStyles.getPropertyValue(`--${view[item].default.icon}`),
          activeImageUrl: themeStyles.getPropertyValue(
            `--${view[item].default.icon}-active`
          ),
          webRoute: view[item].default.webRoute,
          subNav: subView,
        })
      }
    }
    return subNavigation;
  }
  public getdefaulterListColumnData() {
    let displayColumnData = [];
    return (displayColumnData = [
      {
        id: 'slno',
        name: 'Serial No.',
      },
      {
        id: 'name',
        name: 'Name',
      },
      {
        id: 'guardian',
        name: 'Guardian',
      },
      {
        id: 'ritwik',
        name: 'Ritwik',
      },
      {
        id: 'updatedBy',
        name: 'Updated By',
      },
      {
        id: 'familyCode',
        name: 'Family Code',
      },
      {
        id: 'address',
        name: 'Address',
      },
      {
        id: 'contact',
        name: 'Contact',
      },
    ]);
  }
  public roleBasedOnLogin() {
    return [
      {
        name: 'field worker',
        isLoginRequired: true,
        hasRoleFW: true,
      },
      {
        name: 'district admin',
        isLoginRequired: true,
        hasRoleDA: true,
      },
      {
        name: 'state admin',
        isLoginRequired: true,
        hasRoleSA: true,
      },
    ];
  }
  public fetchManageUsersData(): any {
    const stateList: any = [];
    const districtsList: { [key: string]: any[] } = {};
    const allPermissions: any = this.fetchPermissionv3ByViewName();
    let isSuperUser = false;
    const countryPerm = allPermissions['INDIA'];
    if (countryPerm?.allStatesPerm.length > 0) {
      isSuperUser = true
    }
    else {
      Object.entries(countryPerm?.statePerms).forEach(([state, stateData]: [any, any]) => {
        stateList.push(state);
        districtsList[state] = [];
        if (stateData?.allDistrictsPerm.length > 0) {
          districtsList[state].push('ALL')
        }
        else {
          Object.entries(stateData?.districtPerms).forEach(([district]) => {
            districtsList[state].push(district);
          })
        }
      })
    }
    return {
      states: stateList,
      districts: districtsList,
      isSuperUser: isSuperUser,
    };
  }
}
